import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { TourLauncherButton } from "@/contexts/TourContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, XCircle, AlertCircle, ExternalLink, Copy, CreditCard,
  Radio, ShieldCheck, RefreshCw, Sparkles, TestTube, Zap,
} from "lucide-react";
import { toast } from "sonner";

/**
 * R157 — Stripe Test Checkout Walkthrough Wizard.
 *
 * A guided 3-step wizard the admin can run pre-golive to prove that
 * Stripe keys + Checkout Sessions + webhook plumbing are all wired.
 *   Step 1: Verify keys (server-side ping)
 *   Step 2: Open a $1 test checkout in a new tab
 *   Step 3: Poll for the webhook event confirming success
 */

const TEST_CARDS = [
  { number: "4242 4242 4242 4242", label: "Approves (Visa)", tone: "emerald" },
  { number: "4000 0025 0000 3155", label: "3D Secure required", tone: "amber" },
  { number: "4000 0000 0000 9995", label: "Declines · insufficient funds", tone: "rose" },
  { number: "4000 0000 0000 0002", label: "Declines · card_declined", tone: "rose" },
];

export default function StripeTestWalkthroughPage() {
  const [step1, setStep1] = useState({ state: "idle", msg: "", detail: null });
  const [step2, setStep2] = useState({ state: "idle", session_id: null, url: null });
  const [step3, setStep3] = useState({ state: "idle", event: null, polled: 0 });
  const [polling, setPolling] = useState(false);

  const runStep1 = useCallback(async () => {
    setStep1({ state: "running", msg: "Pinging Stripe API…" });
    try {
      const { data } = await api.get("/admin/stripe/test/status");
      setStep1({
        state: data.ok ? "success" : "error",
        msg: data.ok ? "Stripe reachable · keys valid" : (data.error || "Stripe ping failed"),
        detail: data,
      });
    } catch (e) {
      setStep1({ state: "error", msg: apiErrorMessage(e) || "Ping failed" });
    }
  }, []);

  const runStep2 = useCallback(async () => {
    setStep2({ state: "running", session_id: null, url: null });
    try {
      const { data } = await api.post("/admin/dues/test-checkout",
        { origin_url: window.location.origin });
      setStep2({ state: "success", session_id: data.session_id, url: data.checkout_url });
      window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      toast.success("Checkout opened in a new tab · use test card 4242 4242 4242 4242");
      // Kick off webhook polling immediately
      setStep3({ state: "waiting", polled: 0, event: null });
      setPolling(true);
    } catch (e) {
      setStep2({ state: "error", session_id: null, url: null });
      toast.error(apiErrorMessage(e) || "Test checkout failed");
    }
  }, []);

  // Poll for the webhook event confirming the checkout completed
  useEffect(() => {
    if (!polling || !step2.session_id) return;
    let stopped = false;
    let count = 0;
    const poll = async () => {
      count += 1;
      try {
        const { data } = await api.get("/admin/stripe/test/webhook-status",
          { params: { session_id: step2.session_id } });
        setStep3((s) => ({ ...s, polled: count }));
        if (data.completed) {
          setStep3({ state: "success", event: data.event, polled: count });
          setPolling(false);
          toast.success("Webhook fired · Stripe pipeline verified");
          return;
        }
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/StripeTestWalkthroughPage.jsx: swallowed', err); }
      if (count >= 60 && !stopped) {  // 60 * 5s = 5 min timeout
        setStep3((s) => ({ ...s, state: "timeout" }));
        setPolling(false);
        return;
      }
      if (!stopped) setTimeout(poll, 5000);
    };
    poll();
    return () => { stopped = true; };
  }, [polling, step2.session_id]);

  const restart = () => {
    setStep1({ state: "idle", msg: "", detail: null });
    setStep2({ state: "idle", session_id: null, url: null });
    setStep3({ state: "idle", event: null, polled: 0 });
    setPolling(false);
  };

  const copyCard = (num) => {
    navigator.clipboard.writeText(num.replace(/\s/g, ""));
    toast.success("Card number copied");
  };

  const allDone = step1.state === "success" && step2.state === "success"
                    && step3.state === "success";

  return (
    <div className="space-y-4" data-testid="stripe-walkthrough-page">
      <PageHeader
        eyebrow="Payments · Preflight"
        title="Stripe Test Walkthrough"
        subtitle="Verify Stripe keys, Checkout Sessions, and webhooks are wired end-to-end before you invite residents to pay."
        actions={<TourLauncherButton name="stripe_walkthrough_tour" />}
      />

      {allDone && (
        <Card className="border-emerald-500/40 bg-emerald-500/[0.06] p-4 flex items-center gap-3"
              data-testid="walkthrough-all-done">
          <Sparkles className="h-5 w-5 text-emerald-300" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-emerald-100">
              Every Stripe pipeline is verified.
            </div>
            <div className="text-xs text-emerald-200/80">
              Residents can now pay dues from their portal, autopay will renew,
              and refunds/disputes will land in your admin drawer.
            </div>
          </div>
          <Button variant="outline" onClick={restart}
                  className="border-emerald-500/40 text-emerald-200 h-9 rounded-full"
                  data-testid="walkthrough-restart">
            <RefreshCw className="h-4 w-4 mr-1" /> Run again
          </Button>
        </Card>
      )}

      {/* STEP 1 — Verify keys */}
      <StepCard
        num={1} title="Verify Stripe keys" icon={ShieldCheck}
        state={step1.state} testid="stripe-step-1"
        subtitle="Reads STRIPE_SECRET_KEY from the pod env and pings Stripe's API."
      >
        <div className="flex items-center gap-3">
          <Button onClick={runStep1} disabled={step1.state === "running"}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                  data-testid="step1-run">
            {step1.state === "running"
              ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Pinging…</>
              : <><Zap className="h-4 w-4 mr-1" /> Run key check</>}
          </Button>
          {step1.state === "success" && (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-200">
              <CheckCircle2 className="h-3 w-3 mr-1" /> {step1.msg}
            </Badge>
          )}
          {step1.state === "error" && (
            <Badge variant="outline" className="border-rose-500/40 text-rose-200">
              <XCircle className="h-3 w-3 mr-1" /> {step1.msg}
            </Badge>
          )}
        </div>
        {step1.detail && step1.state === "success" && (
          <div className="mt-3 text-xs font-mono text-white/50 rounded-md border border-white/5 bg-white/[0.02] p-2">
            mode: {step1.detail.mode} · account: {step1.detail.account_id || "—"} · publishable_key: {step1.detail.publishable_key_masked || "—"}
          </div>
        )}
      </StepCard>

      {/* STEP 2 — Open Test Checkout */}
      <StepCard
        num={2} title="Open a $1 test checkout" icon={CreditCard}
        state={step2.state} testid="stripe-step-2"
        subtitle="Creates a real Stripe Checkout Session tagged `admin_test` (skipped by ledger)."
        disabled={step1.state !== "success"}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={runStep2} disabled={step1.state !== "success" || step2.state === "running"}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                  data-testid="step2-run">
            {step2.state === "running"
              ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Creating session…</>
              : <><TestTube className="h-4 w-4 mr-1" /> Open $1 checkout</>}
          </Button>
          {step2.state === "success" && step2.url && (
            <a href={step2.url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-xs text-orange-200 hover:text-orange-100 underline"
               data-testid="step2-reopen">
              <ExternalLink className="h-3 w-3" /> Reopen tab
            </a>
          )}
        </div>

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Use one of these test cards</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {TEST_CARDS.map((c) => (
              <div key={c.number}
                    className={`flex items-center justify-between rounded-md border p-2 bg-white/[0.02] border-${c.tone}-500/30`}
                    data-testid={`test-card-${c.tone}`}>
                <div>
                  <div className="font-mono text-sm">{c.number}</div>
                  <div className={`text-[10px] text-${c.tone}-300`}>{c.label}</div>
                </div>
                <button onClick={() => copyCard(c.number)}
                        className="text-white/40 hover:text-white p-1"
                        aria-label={`Copy ${c.number}`}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-white/50 mt-2">
            Use any future expiry date · any CVC · any billing ZIP. On the test dashboard you'll see the $1 charge appear as a test-mode payment (not real money).
          </div>
        </div>
      </StepCard>

      {/* STEP 3 — Confirm Webhook */}
      <StepCard
        num={3} title="Confirm webhook delivery" icon={Radio}
        state={step3.state} testid="stripe-step-3"
        subtitle="After you finish (or cancel) the checkout, we poll `checkout.session.completed` webhooks tagged with your session id."
        disabled={step2.state !== "success"}
      >
        {step3.state === "waiting" && (
          <div className="flex items-center gap-3">
            <RefreshCw className="h-4 w-4 text-orange-400 animate-spin" />
            <div className="text-sm text-white/70">
              Waiting for webhook · polled {step3.polled}/60 (every 5s · times out in {Math.max(0, 300 - step3.polled * 5)}s)
            </div>
          </div>
        )}
        {step3.state === "success" && step3.event && (
          <div>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-200">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Webhook received
            </Badge>
            <div className="mt-2 text-xs font-mono text-white/50 rounded-md border border-white/5 bg-white/[0.02] p-2">
              event_id: {step3.event.id || "—"} · type: {step3.event.type || "—"} · amount: ${step3.event.amount || "1.00"} · received_at: {step3.event.received_at || "—"}
            </div>
          </div>
        )}
        {step3.state === "timeout" && (
          <div>
            <Badge variant="outline" className="border-amber-500/40 text-amber-200">
              <AlertCircle className="h-3 w-3 mr-1" /> Timed out after 5 min
            </Badge>
            <div className="text-xs text-white/60 mt-2">
              Check the Stripe Dashboard → Developers → Webhooks endpoint URL points to <code>{window.location.origin}/api/webhooks/stripe</code>, and that <code>checkout.session.completed</code> is subscribed.
            </div>
          </div>
        )}
        {step3.state === "idle" && step2.state !== "success" && (
          <div className="text-xs text-white/50">Complete step 2 to start webhook polling.</div>
        )}
      </StepCard>
    </div>
  );
}


function StepCard({ num, title, subtitle, icon: Icon, state, testid, children, disabled }) {
  const stateBadge = {
    success: <Badge variant="outline" className="border-emerald-500/40 text-emerald-200 text-[10px]">Complete</Badge>,
    error: <Badge variant="outline" className="border-rose-500/40 text-rose-200 text-[10px]">Failed</Badge>,
    running: <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">Running</Badge>,
    waiting: <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">Waiting</Badge>,
    timeout: <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">Timeout</Badge>,
    idle: null,
  }[state];
  return (
    <Card data-tour={testid} data-testid={testid}
           className={`p-5 border-white/10 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
          <span className="text-xs font-bold text-orange-300">{num}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-orange-300" />}
            <div className="text-base font-semibold">{title}</div>
            {stateBadge}
          </div>
          <div className="text-xs text-white/50 mt-0.5">{subtitle}</div>
        </div>
      </div>
      <div className="pl-11">{children}</div>
    </Card>
  );
}
